/// <reference types="w3c-web-usb" />

declare global {
  interface USBDevice extends EventTarget {}
}

const CDC_SET_LINE_CODING = 0x20;
const CDC_SET_CONTROL_LINE_STATE = 0x22;

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 500;
const OPERATION_TIMEOUT_MS = 5000;

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number = OPERATION_TIMEOUT_MS, operationName: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`${operationName} timed out after ${timeoutMs}ms`)), timeoutMs),
    ),
  ]);
}

async function withRetry<T>(operation: (attempt: number) => Promise<T>, maxRetries: number = MAX_RETRIES, operationName: string): Promise<T> {
  let lastError: Error | undefined;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation(attempt);
    } catch (error) {
      lastError = error as Error;
      console.warn(`[USB-Retry] ${operationName} attempt ${attempt + 1}/${maxRetries} failed:`, error);
      
      if (attempt < maxRetries - 1) {
        // Exponential backoff: 500ms, 1000ms, 2000ms...
        const delay = RETRY_DELAY_MS * Math.pow(2, attempt);
        console.log(`[USB-Retry] Retrying in ${delay}ms...`);
        await sleep(delay);
      }
    }
  }
  
  throw new Error(`${operationName} failed after ${maxRetries} attempts: ${lastError?.message}`);
}

async function setLineCoding(device: USBDevice, interfaceIndex: number, baudRate: number): Promise<void> {
  const data = new ArrayBuffer(7);
  const view = new DataView(data);
  view.setUint32(0, baudRate, true);
  view.setUint8(4, 0); // stop bits: 1
  view.setUint8(5, 0); // parity: none
  view.setUint8(6, 8); // data bits: 8

  await device.controlTransferOut({
    requestType: 'class',
    recipient: 'interface',
    request: CDC_SET_LINE_CODING,
    value: 0,
    index: interfaceIndex,
  }, data);
}

async function setControlLineState(device: USBDevice, interfaceIndex: number, dtr: boolean, rts: boolean): Promise<void> {
  const value = (dtr ? 1 : 0) | (rts ? 2 : 0);
  await device.controlTransferOut({
    requestType: 'class',
    recipient: 'interface',
    request: CDC_SET_CONTROL_LINE_STATE,
    value: value,
    index: interfaceIndex,
  });
}

/**
 * Safely release all claimed interfaces and close the device
 */
export async function safeRelease(device: USBDevice): Promise<void> {
  if (!device || !device.opened) return;
  
  try {
    const config = device.configuration;
    if (config) {
      for (let i = 0; i < config.interfaces.length; i++) {
        try {
          await device.releaseInterface(i);
          console.log(`[USB] Released interface ${i}`);
        } catch {
          // Ignore release errors
        }
      }
    }
  } catch {
    // Ignore errors during release
  }
  
  try {
    await device.close();
    console.log('[USB] Device closed');
  } catch {
    // Ignore close errors
  }
}

export function isWebUSBSupported(): boolean {
  return typeof navigator !== 'undefined' && 'usb' in navigator;
}

export async function getPairedDevices(): Promise<PairedDevice[]> {
  if (!isWebUSBSupported()) return [];
  try {
    const devices = await navigator.usb.getDevices();
    return devices.map(d => ({
      device: d,
      productName: d.productName ?? 'Unknown Device',
      manufacturer: d.manufacturerName ?? 'Unknown',
    }));
  } catch (error) {
    console.error('[USB] Failed to get paired devices:', error);
    return [];
  }
}

export interface PairedDevice {
  device: USBDevice;
  productName: string;
  manufacturer: string;
}

const CONNECTION_BAUD_OPTIONS = [9600, 19200, 38400, 57600, 115200];

export interface ConnectionSettings {
  baudRate: number;
}

// Serial reading loop - continuously reads and logs all incoming data
let serialReadingActive = false;
let serialReadRetryCount = 0;
const MAX_SERIAL_READ_RETRIES = 5;

async function startSerialReadingLoop(device: USBDevice): Promise<void> {
  const config = device.configuration;
  if (!config || config.interfaces.length === 0) {
    console.warn('[USB-Serial] No interfaces available for serial reading');
    return;
  }

  // Find the data interface and bulk IN endpoint
  let dataInterfaceIndex = 0;
  let endpointIn: number | null = null;

  // Search all interfaces for bulk IN endpoints
  for (let i = 0; i < config.interfaces.length; i++) {
    const iface = config.interfaces[i];
    for (const ep of iface.alternate.endpoints) {
      if (ep.type === 'bulk' && ep.direction === 'in') {
        endpointIn = ep.endpointNumber;
        dataInterfaceIndex = i;
        break;
      }
    }
    if (endpointIn !== null) break;
  }

  if (endpointIn === null) {
    console.warn('[USB-Serial] No bulk IN endpoint found');
    return;
  }

  console.log(`[USB-Serial] Starting read loop on interface ${dataInterfaceIndex}, endpoint ${endpointIn}`);
  serialReadingActive = true;
  serialReadRetryCount = 0;

  // Read loop
  while (serialReadingActive && device.opened) {
    try {
      const result = await Promise.race([
        device.transferIn(endpointIn!, 64),
        new Promise<USBInTransferResult>((_, reject) =>
          setTimeout(() => reject(new Error('transferIn timeout')), 1000),
        ),
      ]) as USBInTransferResult;

      // Reset retry count on successful read
      serialReadRetryCount = 0;

      if (result.data && result.data.byteLength > 0) {
        const decoder = new TextDecoder();
        const text = decoder.decode(result.data);
        console.log('[USB-Serial RX]', text);
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'transferIn timeout') {
        // Timeout is normal, just continue loop
        continue;
      }
      
      if (!device.opened) {
        console.log('[USB-Serial] Device closed, stopping read loop');
        break;
      }
      
      serialReadRetryCount++;
      console.error(`[USB-Serial] Read error (attempt ${serialReadRetryCount}/${MAX_SERIAL_READ_RETRIES}):`, err);
      
      if (serialReadRetryCount >= MAX_SERIAL_READ_RETRIES) {
        console.error('[USB-Serial] Too many read errors, stopping read loop');
        break;
      }
      
      // Exponential backoff before retry: 100ms, 200ms, 400ms...
      const delay = 100 * Math.pow(2, serialReadRetryCount - 1);
      console.log(`[USB-Serial] Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  console.log('[USB-Serial] Read loop stopped');
}

export async function openDevice(
  device: USBDevice,
  baudRate: number,
  onDisconnect: () => void,
): Promise<{ name: string; manufacturer: string }> {
  console.log('[USB] Opening device:', {
    productName: device.productName,
    manufacturerName: device.manufacturerName,
    vendorId: device.vendorId.toString(16).padStart(4, '0'),
    productId: device.productId.toString(16).padStart(4, '0'),
    opened: device.opened,
    configured: device.configuration !== null,
  });

  // Step 1: Ensure device is in a clean state
  if (device.opened) {
    console.warn('[USB] Device is already opened. Attempting to close first...');
    await safeRelease(device);
    // Small delay to allow the OS to process the close
    await sleep(100);
  }

  // Step 2: Open device with retry
  await withRetry(
    async () => {
      await device.open();
      console.log('[USB] Device opened successfully');
    },
    MAX_RETRIES,
    'openDevice'
  );

  // Step 3: Select configuration
  try {
    if (device.configuration === null) {
      console.log('[USB] Selecting configuration 1...');
      // Try to select configuration 1, but also try configuration 0 if that fails
      try {
        await withTimeout(device.selectConfiguration(1), OPERATION_TIMEOUT_MS, 'selectConfiguration(1)');
      } catch {
        console.warn('[USB] Failed to select configuration 1, trying configuration 0...');
        try {
          await withTimeout(device.selectConfiguration(0), OPERATION_TIMEOUT_MS, 'selectConfiguration(0)');
        } catch {
          throw new Error('Failed to select any configuration');
        }
      }
      console.log('[USB] Configuration selected');
    } else {
      console.log('[USB] Device already configured');
    }
  } catch (error) {
    console.error('[USB] Failed to select configuration:', error);
    await safeRelease(device);
    throw error;
  }

  const ifaceConfig = device.configuration!;
  console.log(`[USB] Device has ${ifaceConfig.interfaces.length} interface(s) in configuration`);

  // Step 4: Claim all interfaces with improved error handling
  const claimedInterfaces: number[] = [];
  
  try {
    for (let i = 0; i < ifaceConfig.interfaces.length; i++) {
      const iface = ifaceConfig.interfaces[i];
      console.log('[USB] Interface', i, ':', {
        interfaceNumber: iface.interfaceNumber,
        claimed: iface.claimed,
        alternates: iface.alternates.length,
      });

      if (!iface.claimed) {
        console.log(`[USB] Claiming interface ${i}...`);
        try {
          await withTimeout(
            device.claimInterface(i),
            OPERATION_TIMEOUT_MS,
            `claimInterface(${i})`
          );
          claimedInterfaces.push(i);
          console.log(`[USB] Interface ${i} claimed successfully`);
        } catch (claimError) {
          console.error(`[USB] Failed to claim interface ${i}:`, claimError);
          
          const errorMsg = (claimError as Error)?.message || '';
          const isKernelDriverError = errorMsg.includes('kernel') || errorMsg.includes('busy') || errorMsg.toLowerCase().includes('denied');
          
          if (isKernelDriverError) {
            const vid = device.vendorId.toString(16).padStart(4, '0');
            const pid = device.productId.toString(16).padStart(4, '0');
            console.error(`[USB] A kernel driver (likely cdc_acm) is holding this interface.
  Solutions:
    1. Create a udev rule to give your user access:
       echo 'SUBSYSTEM=="usb", ATTR{idVendor}=="${vid}", ATTR{idProduct}=="${pid}", MODE="0666", GROUP="plugdev"' | sudo tee /etc/udev/rules.d/99-webusb.rules
       sudo udevadm control --reload-rules && sudo udevadm trigger
    2. Unplug and replug the device after creating the rule
    3. Close any other applications using this device
    4. On Windows, ensure no COM port driver is bound to this device`);
          }
          
          throw claimError;
        }
      }
    }
  } catch (error) {
    console.error('[USB] Failed to claim interfaces, cleaning up...');
    await safeRelease(device);
    throw error;
  }

  // Step 5: Configure CDC-ACM parameters
  // Use the first interface for CDC control (interface 0 is typically the control interface)
  const interfaceNumber = ifaceConfig.interfaces[0].interfaceNumber;

  try {
    console.log('[USB] Setting line coding, baudRate:', baudRate);
    await withTimeout(
      setLineCoding(device, interfaceNumber, baudRate),
      OPERATION_TIMEOUT_MS,
      'setLineCoding'
    );
    console.log('[USB] Line coding set successfully');

    console.log('[USB] Setting control line state (DTR=true, RTS=true)...');
    await withTimeout(
      setControlLineState(device, interfaceNumber, true, true),
      OPERATION_TIMEOUT_MS,
      'setControlLineState'
    );
    console.log('[USB] Control line state set successfully');
  } catch (error) {
    console.error('[USB] Failed to configure CDC-ACM parameters:', error);
    await safeRelease(device);
    throw error;
  }

  console.log('[USB] Device setup complete');

  // Step 6: Set up disconnect monitoring
  const disconnectHandler = (event: USBConnectionEvent) => {
    if (event.device === device) {
      console.log('[USB] Device disconnected');
      onDisconnect();
    }
  };
  navigator.usb.addEventListener('disconnect', disconnectHandler);

  // Store the handler reference on the device for cleanup
  (device as any).__usbDisconnectHandler = disconnectHandler;

  // Step 7: Start serial data reading loop
  startSerialReadingLoop(device);

  return {
    name: device.productName ?? 'Unknown Device',
    manufacturer: device.manufacturerName ?? 'Unknown',
  };
}

export async function closeDevice(device: USBDevice): Promise<void> {
  console.log('[USB] Closing device...');
  
  // Stop serial reading loop
  serialReadingActive = false;

  // Remove the disconnect handler we added to navigator.usb
  const disconnectHandler = (device as any).__usbDisconnectHandler;
  if (disconnectHandler) {
    navigator.usb.removeEventListener('disconnect', disconnectHandler);
    delete (device as any).__usbDisconnectHandler;
  }
  
  // Use safeRelease to properly clean up all interfaces
  await safeRelease(device);
  
  console.log('[USB] Device closed');
}
